import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, MinusCircle, Loader2, Rocket, ShieldAlert, KeyRound, FileCode2, RefreshCw, Smartphone, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const fmtDateTime = (s) => (s ? `${fmtDate(s)} ${s.slice(11, 16)}` : "");

const CRED_FIELDS = [
  ["live_efatura_username", "Canlı web servis kullanıcı adı (ör. entegrasyon.sistem)", "text"],
  ["live_efatura_password", "Canlı web servis parolası", "password"],
  ["live_earsiv_username", "Canlı e-Arşiv kullanıcı adı", "text"],
  ["live_earsiv_password", "Canlı e-Arşiv parolası", "password"],
  ["live_efatura_wsdl", "Canlı e-Fatura WSDL (boş = QNB standart adresi)", "text"],
  ["live_earsiv_wsdl", "Canlı e-Arşiv WSDL (boş = QNB standart adresi)", "text"],
];
const XSLT_FIELDS = [
  ["efatura_xslt", "e-Fatura görsel şablonu (portaldaki XSLT adı)", "efat.xslt"],
  ["earsiv_xslt", "e-Arşiv görsel şablonu (portaldaki XSLT adı)", "E ARŞİV.xslt"],
];

const Icon = ({ ok }) => ok === true ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : ok === false ? <XCircle className="h-5 w-5 text-red-500 shrink-0" /> : <MinusCircle className="h-5 w-5 text-muted-foreground shrink-0" />;

// 90 gün parola sayacı kartı
function PasswordAge({ age }) {
  if (!age?.set_at) {
    return <div className="rounded-lg border p-3 text-xs text-muted-foreground flex gap-2 items-start" data-testid="qnb-password-age"><CalendarClock className="h-4 w-4 shrink-0 mt-0.5" /><span>Parola sayacı: canlı parola kaydedilince başlar. QNB web servis parolası <b>90 günde bir</b> değiştirilmelidir; süre yaklaşınca burada ve panoda uyarı görürsünüz.</span></div>;
  }
  const cls = age.expired ? "border-red-300 bg-red-50 text-red-800" : age.warn ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-800";
  return (
    <div className={`rounded-lg border p-3 text-xs flex gap-2 items-start ${cls}`} data-testid="qnb-password-age">
      <CalendarClock className="h-4 w-4 shrink-0 mt-0.5" />
      <div>
        <div className="font-bold">
          {age.expired ? `Parola ${age.days} gündür değiştirilmedi — 90 gün süresi DOLDU` : `Parola ${age.days} gün önce güncellendi · kalan ${age.remaining} gün`}
          <span className="font-normal"> (son güncelleme {fmtDate(age.set_at)}{age.expires_at ? ` · son geçerlilik ${fmtDate(age.expires_at)}` : ""})</span>
        </div>
        <div className="mt-0.5">Parolayı QNB portalında değiştirdikten sonra yukarıdaki alana yeni parolayı yazıp <b>Canlı Kimlikleri Kaydet</b>'e basın; sayaç sıfırlanır.</div>
      </div>
    </div>
  );
}

// Canlıya geçiş hazırlık kontrolü — ortamı DEĞİŞTİRMEZ, yalnızca sorgular
export default function QnbLiveCheck({ qnb, onChanged }) {
  const [creds, setCreds] = useState({});
  const [sameUser, setSameUser] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [tplBusy, setTplBusy] = useState(false);

  useEffect(() => {
    const s = qnb?.settings || {};
    const next = Object.fromEntries([...CRED_FIELDS, ...XSLT_FIELDS].map(([k]) => [k, s[k] || ""]));
    setCreds(next);
    setSameUser(!s.live_earsiv_username || s.live_earsiv_username === s.live_efatura_username);
    if (qnb?.live_check?.items?.length) setResult(qnb.live_check);
  }, [qnb]);

  const set = (k, v) => setCreds((c) => {
    const n = { ...c, [k]: v };
    if (sameUser && k === "live_efatura_username") n.live_earsiv_username = v;
    if (sameUser && k === "live_efatura_password") n.live_earsiv_password = v;
    return n;
  });
  const toggleSame = (v) => {
    setSameUser(!!v);
    if (v) setCreds((c) => ({ ...c, live_earsiv_username: c.live_efatura_username, live_earsiv_password: c.live_efatura_password }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...creds };
      if (sameUser) { payload.live_earsiv_username = payload.live_efatura_username; payload.live_earsiv_password = payload.live_efatura_password; }
      await api.put("/qnb/settings", payload);
      toast.success("Canlı kimlikler ve şablon adları kaydedildi (parola maskeli saklanır, 90 gün sayacı başladı)");
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/qnb/live-check");
      setResult(data);
      data.passed ? toast.success("Tüm kontroller geçti — sistem canlıya geçmeye HAZIR") : toast.warning("Kontroller tamamlandı — bazı maddeler eksik (aşağıda)");
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRunning(false); }
  };
  const fetchTemplates = async () => {
    setTplBusy(true);
    try { const { data } = await api.get("/qnb/templates", { params: { urun: "EFATURA" } }); setTpl(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setTplBusy(false); }
  };

  const okCount = (result?.items || []).filter((i) => i.ok === true).length;
  const isLive = qnb?.mode === "live";
  const fixed = qnb?.live_fixed_series || {};
  const visibleCreds = CRED_FIELDS.filter(([k]) => !(sameUser && (k === "live_earsiv_username" || k === "live_earsiv_password")));

  return (
    <div className="rounded-xl border-2 border-dashed p-4 space-y-4" data-testid="qnb-live-check">
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shrink-0"><Rocket className="h-5 w-5" /></span>
        <div className="min-w-0">
          <div className="font-head font-bold text-lg leading-tight">Canlıya Geçiş Hazırlığı · Canlı Kimlikler</div>
          <p className="text-xs text-muted-foreground mt-1">
            Şu an sistem <b className={isLive ? "text-red-600" : "text-emerald-700"}>{isLive ? "CANLI" : "TEST"}</b> ortamında{isLive ? "" : " — kesilen hiçbir belge gerçek GİB kaydı değildir"}.
            QNB portalında tanımlı web servis kullanıcısını (ör. <b>entegrasyon.sistem</b>) buraya girin; "Kontrolleri Çalıştır" canlı sunucuya <b>yalnızca sorgu</b> atar (belge göndermez, ortamı değiştirmez).
            Tüm maddeler ✓ olmadan Ortam seçiminde "CANLI" seçilemez.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]" data-testid="qnb-live-fixed">
            <Badge variant="outline">Canlı seri · e-Fatura <b className="ml-1">{fixed.series || "GEA"}</b></Badge>
            <Badge variant="outline">e-Arşiv <b className="ml-1">{fixed.earsiv_series || "GAA"}</b></Badge>
            <Badge variant="outline">e-İrsaliye <b className="ml-1">{fixed.irsaliye_series || "GI"}</b></Badge>
            <span className="text-muted-foreground self-center">(canlıda sabit — başka seriye kesilmez)</span>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 flex gap-2" data-testid="qnb-sms-info">
        <Smartphone className="h-4 w-4 shrink-0 mt-0.5" />
        <div><b>SMS doğrulaması hakkında:</b> Web servis (entegrasyon) girişinde SMS kodu istenmez; SMS yalnızca portala tarayıcıdan girişte kullanılır. Kullanıcı portalda <b>Web Servis Kullanıcısı</b> olarak işaretli ve SMS/2 aşamalı doğrulaması kapalı olmalıdır. Aşağıdaki kontrol bunu gerçek canlı sunucuda sınar; QNB reddederse nedeni Türkçe açıklanır.</div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox checked={sameUser} onCheckedChange={toggleSame} id="qnb-same-user" data-testid="qnb-live-same-user" />
        <Label htmlFor="qnb-same-user" className="text-sm">Canlıda tek kullanıcı — e-Arşiv için de aynı kullanıcı adı/parolayı kullan</Label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visibleCreds.map(([k, label, type]) => (
          <div key={k} className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">{type === "password" && <KeyRound className="h-3 w-3" />}{label}</Label>
            <Input type={type} value={creds[k] || ""} onChange={(e) => set(k, e.target.value)} autoComplete="off" data-testid={`qnb-${k.replace(/_/g, "-")}`} placeholder={type === "password" ? "••••••••" : ""} />
          </div>
        ))}
      </div>
      <PasswordAge age={qnb?.password_age} />

      <div className="rounded-lg border p-3 space-y-2" data-testid="qnb-xslt-block">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><FileCode2 className="h-3.5 w-3.5" />GİB Görsel Şablonları (portalda yüklü XSLT adları)</div>
          <Button size="sm" variant="outline" onClick={fetchTemplates} disabled={tplBusy} data-testid="qnb-templates-fetch">{tplBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}Şablonları QNB'den getir ({isLive ? "canlı" : "test"} hesabı)</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {XSLT_FIELDS.map(([k, label, ph]) => (
            <div key={k} className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
              <Input value={creds[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={`${ph} (canlı varsayılan)`} data-testid={`qnb-${k.replace(/_/g, "-")}`} />
            </div>
          ))}
        </div>
        {tpl && (
          <div className="text-xs space-y-1" data-testid="qnb-templates-list">
            <div className="text-muted-foreground">{tpl.mode === "live" ? "Canlı" : "Test"} hesabındaki e-Fatura şablonları ({tpl.names.length}):</div>
            <div className="flex flex-wrap gap-1.5">
              {tpl.names.length === 0 && <span className="text-muted-foreground">Şablon bulunamadı.</span>}
              {tpl.names.map((n) => <button key={n} type="button" className="px-2 py-1 rounded border bg-white hover:bg-accent font-mono text-[11px]" onClick={() => set("efatura_xslt", n)} data-testid={`qnb-template-${n}`}>{n}</button>)}
            </div>
            <div className="text-muted-foreground">Tıklayınca e-Fatura şablon alanına yazılır. e-Arşiv şablonu "Kontrolleri Çalıştır" ile canlı e-Arşiv hesabında doğrulanır (AE00012 = ad bulunamadı).</div>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">Boş bırakılırsa canlıda <b>efat.xslt</b> / <b>E ARŞİV.xslt</b> kullanılır; test ortamında boş = QNB varsayılan şablonu. Ad birebir (boşluk, büyük/küçük harf, Türkçe karakter) portaldaki gibi olmalıdır.</p>
      </div>

      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="outline" onClick={save} disabled={saving} data-testid="qnb-live-save">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1.5" />}Canlı Kimlikleri Kaydet</Button>
        <Button onClick={run} disabled={running} data-testid="qnb-live-run">{running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldAlert className="h-4 w-4 mr-1.5" />}Kontrolleri Çalıştır</Button>
      </div>

      {result && (
        <div className="space-y-2" data-testid="qnb-live-result">
          <div className={`rounded-lg p-3 text-sm font-bold flex items-center gap-2 ${result.passed ? "bg-emerald-50 text-emerald-800 border border-emerald-300" : "bg-amber-50 text-amber-900 border border-amber-300"}`} data-testid="qnb-live-summary">
            {result.passed ? <CheckCircle2 className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
            {result.passed ? "HAZIR — tüm kontroller geçti; Ortam → CANLI seçilebilir (24 saat geçerli)" : `HAZIR DEĞİL — ${okCount}/${(result.items || []).length} madde geçti`}
            <span className="ml-auto text-xs font-normal">{result.at ? fmtDateTime(result.at) : ""}{result.by ? ` · ${result.by}` : ""}</span>
          </div>
          <ul className="divide-y rounded-lg border">
            {(result.items || []).map((it) => (
              <li key={it.key} className="p-3 flex gap-3 text-sm" data-testid={`qnb-live-item-${it.key}`}>
                <Icon ok={it.ok} />
                <div className="min-w-0">
                  <div className="font-semibold">{it.label}</div>
                  {it.detail && <div className="text-xs text-muted-foreground break-all">{it.detail}</div>}
                  {it.hint && it.ok !== true && <div className="text-xs text-amber-700 mt-0.5">→ {it.hint}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
